import React, { useRef, useMemo } from "react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import { useVirtualizer } from "@tanstack/react-virtual";
import ManualLocationItem from "src/views/inventory/requests/orders/ManualLocationItem";
import useAuth from "src/hooks/useAuth";

export default function InventoryTable({
  filteredInventoryItems,
  showBarcodeColumn,
  inventoryRequest,
  advancedPickingMode,
  selectedRowId,
  setSelectedRowId,
  handlePickKit,
  project,
  warehouseLookup = {},
  hasMultipleWarehouses = false,
  orderType = "inventoryRequest",
  showReturnToLocation = false,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const showStagedStatus = orderType === "inventoryRequest" && settings?.scan_to_staging_area_for_inventory_request;
  const parentRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: filteredInventoryItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 85,
    overscan: 10,
    lanes: 1,
    getItemKey: useMemo(
      () => index => {
        const item = filteredInventoryItems[index];
        return `${item?.id || index}`;
      },
      [filteredInventoryItems]
    ),
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;

  return (
    <Box
      ref={parentRef}
      sx={{
        maxHeight: "500px",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "auto",
        willChange: "scroll-position",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="center" width={90}>
              Image
            </TableCell>
            <TableCell align="center">Item ID</TableCell>
            {showBarcodeColumn && <TableCell align="center">Barcode</TableCell>}
            <TableCell align="center">Description</TableCell>
            <TableCell align="center" width={80}>
              Quantity Requested
            </TableCell>
            <TableCell align="center" width={120}>
              Pick Location(s)
            </TableCell>
            {hasMultipleWarehouses && (
              <TableCell align="center" width={120}>
                Picking From
              </TableCell>
            )}
            {showStagedStatus && (
              <TableCell align="center" width={160}>
                Staged Status
              </TableCell>
            )}
            <TableCell align="center" width={160}>
              {orderType === "internalWorkOrder" ? "Stage Status" : "Pick Status"}
            </TableCell>
            {orderType === "internalWorkOrder" && showReturnToLocation && (
              <TableCell align="center">Return to Location</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {paddingTop > 0 && (
            <TableRow style={{ height: paddingTop }}>
              <TableCell
                style={{ padding: 0, border: 0 }}
                colSpan={
                  6 +
                  (showBarcodeColumn ? 1 : 0) +
                  (hasMultipleWarehouses ? 1 : 0) +
                  (showStagedStatus ? 1 : 0) +
                  (showReturnToLocation ? 1 : 0)
                }
              />
            </TableRow>
          )}
          {virtualRows.map(virtualRow => {
            const { asset, rackLocations, ...item } = filteredInventoryItems[virtualRow.index];
            return (
              <ManualLocationItem
                key={virtualRow.key}
                asset={asset}
                item={item}
                rackLocations={rackLocations}
                inventoryRequest={inventoryRequest}
                advancedPickingMode={advancedPickingMode}
                showBarcodeColumn={showBarcodeColumn}
                selected={selectedRowId === item.id}
                onClick={() => setSelectedRowId(item.id)}
                onPickKit={(kitAssetId, rackLocations) =>
                  handlePickKit(kitAssetId, rackLocations, item)
                }
                project={project}
                warehouseLookup={warehouseLookup}
                hasMultipleWarehouses={hasMultipleWarehouses}
                orderType={orderType}
                showReturnToLocation={showReturnToLocation}
                showStagedStatus={showStagedStatus}
              />
            );
          })}
          {paddingBottom > 0 && (
            <TableRow style={{ height: paddingBottom }}>
              <TableCell
                style={{ padding: 0, border: 0 }}
                colSpan={
                  6 +
                  (showBarcodeColumn ? 1 : 0) +
                  (hasMultipleWarehouses ? 1 : 0) +
                  (showStagedStatus ? 1 : 0) +
                  (showReturnToLocation ? 1 : 0)
                }
              />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
